import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/base/SEOHead";
import SchemaOrg from "@/components/base/SchemaOrg";
import { articles, comments } from "@/mocks/articles";

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900">文章未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 font-label cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleComments = comments.filter((c) => c.articleId === article.id);
  const related = articles
    .filter((a) => article.relatedArticles.includes(a.id))
    .slice(0, 3);

  const paragraphs = article.content.split("\n\n").filter((p) => p.trim());

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(",")}
        canonicalUrl={`/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        articlePublishedTime={article.publishDate}
        articleAuthor={article.author}
        articleSection={article.categoryName}
        articleTags={article.tags}
      />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          image: article.image,
          datePublished: article.publishDate,
          author: {
            "@type": "Organization",
            name: article.author,
          },
          publisher: {
            "@type": "Organization",
            name: "51爆料",
            logo: {
              "@type": "ImageObject",
              url: "https://www.51baoliao.com/logo.png",
            },
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://www.51baoliao.com/article/${article.id}`,
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { "@type": "ListItem", position: 1, item: { "@id": "https://www.51baoliao.com", name: "首页" } },
            { "@type": "ListItem", position: 2, item: { "@id": `https://www.51baoliao.com/category/${article.category}`, name: article.categoryName } },
            { "@type": "ListItem", position: 3, item: { "@id": `https://www.51baoliao.com/article/${article.id}`, name: article.title } },
          ],
        }}
      />

      <main>
        <Navbar />

        <section className="relative w-full h-[300px] md:h-[450px] overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
          <div className="absolute bottom-0 left-0 right-0 z-10 px-4 md:px-6 pb-8 md:pb-12">
            <div className="max-w-4xl mx-auto">
              <span className="inline-block px-3 py-1 text-xs font-label bg-primary-500 text-background-50 rounded-full whitespace-nowrap mb-3">
                {article.categoryName}
              </span>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-black text-background-50 leading-tight">
                {article.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-background-50/70">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.viewCount >= 10000
                    ? `${(article.viewCount / 10000).toFixed(1)}万阅读`
                    : `${article.viewCount}阅读`}
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <Breadcrumb items={[
              { label: article.categoryName, to: `/category/${article.category}` },
              { label: article.title },
            ]} />

            <article className="mt-6">
              <div className="prose-custom">
                {paragraphs.map((paragraph, index) => (
                  <p key={index} className="text-foreground-700 leading-relaxed mb-4 text-sm md:text-base">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-background-300/60">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs font-label bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </article>

            {articleComments.length > 0 && (
              <section className="mt-10">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-5">
                  网友评论 ({articleComments.length})
                </h3>
                <div className="space-y-4">
                  {articleComments.map((comment) => (
                    <div
                      key={comment.id}
                      className="bg-background-100 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 font-label text-xs font-medium">
                            {comment.user.charAt(0)}
                          </span>
                          <div>
                            <p className="text-sm font-label font-medium text-foreground-900">{comment.user}</p>
                            <p className="text-xs text-foreground-400">{comment.userTag}</p>
                          </div>
                        </div>
                        <span className="text-xs text-foreground-400">{comment.date}</span>
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed">{comment.content}</p>
                      <div className="mt-2 flex items-center gap-1 text-xs text-foreground-400">
                        <i className="ri-thumb-up-line"></i>
                        <span>{comment.likes}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </section>

        {related.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <h3 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                相关爆料
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {related.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group cursor-pointer"
                  >
                    <article className="bg-background-50 rounded-lg overflow-hidden transition-transform duration-300 group-hover:-translate-y-1">
                      <div className="h-44 overflow-hidden">
                        <img
                          src={rel.image}
                          alt={rel.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <span className="text-xs font-label text-primary-500">{rel.categoryName}</span>
                        <h4 className="mt-1 font-label font-medium text-foreground-900 text-sm leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {rel.title}
                        </h4>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}